import { SITE_CALCULATOR } from '@/config/site'
import { formatNumber } from '@/utils/format'
import { makeAutoObservable } from 'mobx'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import { Tariff } from '@/services/Api/generated'

export const TariffNameMap: { [key in Tariff]: string } = {
  [Tariff.Self]: 'Удобный для самостоятельных',
  [Tariff.Optimal]: 'Оптимальный для уверенных',
  [Tariff.AllInclusive]: 'Все включено',
  [Tariff.WithGuarantee]: 'С гарантией',
}

export const TariffPriceMap: { [key in Tariff]: number } = {
  [Tariff.Self]: SITE_CALCULATOR.tariff.SELF,
  [Tariff.Optimal]: SITE_CALCULATOR.tariff.OPTIMAL,
  [Tariff.AllInclusive]: SITE_CALCULATOR.tariff.ALL_INCLUSIVE,
  [Tariff.WithGuarantee]: SITE_CALCULATOR.tariff.WITH_GUARANTEE,
}

export enum TrademarkType {
  WORD = 'WORD',
  IMAGE = 'IMAGE',
  COMBINE = 'COMBINE',
}

export enum RegistrationPeriod {
  STANDARD = 'STANDARD',
  ACCELERATED = 'ACCELERATED',
}

export const TrademarkTypeNameMap: { [key in TrademarkType]: string } = {
  [TrademarkType.WORD]: 'Словесный',
  [TrademarkType.IMAGE]: 'Изобразительный',
  [TrademarkType.COMBINE]: 'Комбинированный',
}

export const RegistrationPeriodNameMap: {
  [key in RegistrationPeriod]: string
} = {
  [RegistrationPeriod.STANDARD]: 'Стандартный: 6-12 мес.',
  [RegistrationPeriod.ACCELERATED]: 'Ускоренный: 2 мес.',
}

export const RegistrationPeriodPriceMap: {
  [key in RegistrationPeriod]: number
} = {
  [RegistrationPeriod.STANDARD]: 0,
  [RegistrationPeriod.ACCELERATED]: SITE_CALCULATOR.accelerated.servicePrice,
}

export const AcceleratedTaxMap = {
  [TrademarkType.WORD]: SITE_CALCULATOR.accelerated.tax.WORD,
  [TrademarkType.IMAGE]: SITE_CALCULATOR.accelerated.tax.IMAGE,
  [TrademarkType.COMBINE]: SITE_CALCULATOR.accelerated.tax.COMBINE,
}

export class CalculatorStore {
  tariff: Tariff = Tariff.Self

  trademarkType: TrademarkType = TrademarkType.WORD

  registrationPeriod: RegistrationPeriod = RegistrationPeriod.STANDARD

  selectedClasses: MktuClassStore[] = []

  get countClasses(): number {
    return this.selectedClasses.length
  }

  get taxForClasses() {
    const defaultTax = SITE_CALCULATOR.taxForSubmitting
    const mktuClassPrice = SITE_CALCULATOR.mktuClassPrice

    const countClasses = this.countClasses

    return (
      defaultTax +
      Math.max((countClasses - 1) * mktuClassPrice, 0) +
      this.taxPerWords
    )
  }

  /**
   * Для каждого класса первые 10 слов бесплатные
   * каждое слово свыше оплачивается дополнительно
   */
  get taxPerWords(): number {
    const mktuWordPrice = SITE_CALCULATOR.mktuWordPrice

    const countAdditionalWords = this.selectedClasses.reduce(
      (acc, mktuClass) => {
        // Свыше 10 слов
        const additionalWords = Math.max(mktuClass.selectedWords.length - 10, 0)
        return acc + additionalWords
      },
      0
    )

    return countAdditionalWords * mktuWordPrice
  }

  get taxCertificate() {
    const countClasses = Number(this.countClasses)
    return (
      SITE_CALCULATOR.taxForIssuance +
      Math.max((countClasses - 5) * SITE_CALCULATOR.mktuClassIssuancePrice, 0)
    )
  }

  get totalTax() {
    return this.taxForClasses + this.taxCertificate + this.acceleratedTax
  }

  get tariffPrice() {
    return TariffPriceMap[this.tariff]
  }

  get registrationPeriodPrice() {
    return RegistrationPeriodPriceMap[this.registrationPeriod]
  }

  get totalServicePrice() {
    return this.tariffPrice + this.registrationPeriodPrice
  }

  get resultSum() {
    return this.totalServicePrice + this.totalTax
  }

  get acceleratedServicePrice() {
    return this.isAccelerated ? SITE_CALCULATOR.accelerated.servicePrice : 0
  }

  get acceleratedTax() {
    return this.isAccelerated ? AcceleratedTaxMap[this.trademarkType] : 0
  }

  get data() {
    return {
      tarif: `Тариф “${TariffNameMap[this.tariff]}”`,
      tm_type: TrademarkTypeNameMap[this.trademarkType],
      term: RegistrationPeriodNameMap[this.registrationPeriod],
      mktu_count: String(this.countClasses),
      total_sum: this.resultSum,
    }
  }

  get tariffName() {
    return TariffNameMap[this.tariff]
  }

  get formatTariffPrice() {
    return formatNumber(this.tariffPrice)
  }

  get formatTaxForClasses() {
    return formatNumber(this.taxForClasses)
  }

  get formatTaxCertificate() {
    return formatNumber(this.taxCertificate)
  }

  get formatTotalTax() {
    return formatNumber(this.totalTax)
  }

  get formatTotalServicePrice() {
    return formatNumber(this.totalServicePrice)
  }

  get formatResultSum() {
    return formatNumber(this.resultSum)
  }

  get formatAcceleratedServicePrice() {
    return formatNumber(this.acceleratedServicePrice)
  }

  get formatAcceleratedTax() {
    return formatNumber(this.acceleratedTax)
  }

  get isAccelerated() {
    return this.registrationPeriod === RegistrationPeriod.ACCELERATED
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true })
  }

  setTariff(tariff: Tariff) {
    this.tariff = tariff
  }

  setTrademarkType(trademarkType: TrademarkType) {
    this.trademarkType = trademarkType
  }

  setRegistrationPeriod(registrationPeriod: RegistrationPeriod) {
    this.registrationPeriod = registrationPeriod
  }

  setSelectedMktuClasses(classes: MktuClassStore[]) {
    this.selectedClasses = classes
  }
}
